// TODO: https://github.com/combinaut/sparkle/issues/32431 rewrites the index controls and deletes this bridge.
import { TestId } from "#/test-ids";

/** Connects an index manager to `sp-index-filters`, if its container contains the app. */
export function attachIndexFiltersBridge(manager) {
  const container = manager.el[0];
  const app = container?.querySelector("sp-index-filters");

  if (!app) {
    return;
  }

  const originalParams = manager.filterUrlParams.bind(manager);
  const originalSummary = manager.refreshAppliedFiltersList.bind(manager);
  let state;

  function currentState() {
    return app.getFilterState?.() ?? state;
  }

  function filterUrlParams() {
    const params = new URLSearchParams(originalParams());
    const current = currentState();

    if (current) {
      for (const name of current.owned) {
        const scalar = name.replace(/\[\]$/, "");
        params.delete(scalar);
        params.delete(`${scalar}[]`);
      }

      for (const [name, value] of current.params) {
        params.append(name, value);
      }

      params.delete("page");
    }

    return params.toString();
  }

  function refreshAppliedFiltersList(...args) {
    originalSummary(...args);
    const summary = container.querySelector("[data-index-table-applied-filters-summary]");
    const current = currentState();

    if (!summary || !current) {
      return;
    }

    const list = summary.querySelector(".applied-filters-list") ?? summary;
    const labels = app.bridgeLabels ?? {};

    for (const filter of current.applied) {
      const breadcrumb = document.createElement("sp-chip");
      breadcrumb.className = "index-filter-crumb";
      breadcrumb.dataset.testid = TestId.admin.indexFilterBreadcrumb;
      breadcrumb.setAttribute("closable", "");
      breadcrumb.setAttribute("close-test-id", TestId.admin.indexFilterRemove);
      const show = document.createElement("sp-button");
      show.setAttribute("variant", "appearance-link");
      show.textContent = filter.description ? `${filter.label}: ${filter.description}` : filter.label;
      show.title = labels.showFilter ?? "";

      function showFilter() {
        app.show();
      }

      function removeFilter() {
        app.clear(filter.param, filter.values);
      }

      show.addEventListener("click", showFilter);
      breadcrumb.addEventListener("close", removeFilter);
      breadcrumb.append(show);
      list.append(breadcrumb);
    }

    const empty = current.applied.length === 0 && !manager.searchFilter?.isActive();
    summary.classList.toggle("--empty", empty);

    if (current.applied.length > 0 && !summary.querySelector("[data-index-filters-clear-all]")) {
      const clear = document.createElement("sp-button");
      clear.dataset.indexFiltersClearAll = "";
      clear.dataset.testid = TestId.admin.indexFiltersClear;
      clear.setAttribute("variant", "appearance-link");
      clear.textContent = labels.clearAll ?? "";
      clear.addEventListener("click", clearFilters);
      summary.append(clear);
    }
  }

  function syncFocusControl() {
    const focus = manager.focusControl?.[0];
    const current = currentState();
    const name = manager.focusControlFilterName;

    if (focus && current?.owned.includes(name)) {
      const value = current.params.find(([param]) => param === name)?.[1];
      focus.checked = String(manager.focusControlCheckedValue) === String(value);
    }
  }

  function handleChange(event) {
    const next = event.detail?.[0];

    if (!Array.isArray(next?.params) || !Array.isArray(next?.owned) || !Array.isArray(next?.applied)) {
      return;
    }

    state = next;
    syncFocusControl();
    refreshAppliedFiltersList();
    manager.refreshTable();
  }

  function handleReady() {
    refreshAppliedFiltersList();
  }

  async function handleFocusModeChange() {
    const name = manager.focusControlFilterName;
    const value = String(manager.focusControlValue);
    const changed = await app.setValue?.(name, value);

    if (!changed) {
      manager.refreshTable();
    }
  }

  function clearFilters() {
    app.clearAll();
  }

  // A page restored from the back-forward cache keeps the app's last edits, which its URL does not carry.
  function handlePageShow(event) {
    if (event.persisted) {
      app.reload();
    }
  }

  manager.filterUrlParams = filterUrlParams;
  manager.refreshAppliedFiltersList = refreshAppliedFiltersList;
  manager.handleFocusModeChange = handleFocusModeChange;
  manager.clearFilters = clearFilters;
  app.addEventListener("change", handleChange);
  app.addEventListener("index-filters-ready", handleReady);
  window.addEventListener("pageshow", handlePageShow);

  function detach() {
    app.removeEventListener("change", handleChange);
    app.removeEventListener("index-filters-ready", handleReady);
    app.removeEventListener("index-filters-disconnected", detach);
    window.removeEventListener("pageshow", handlePageShow);
  }

  app.addEventListener("index-filters-disconnected", detach);
}
